import React from "react";
import { MapPin, Phone, Mail, Clock, Loader, Send, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const ContactForm = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="5149" className="flex flex-col items-center justify-center h-full min-h-[400px] text-center p-8 space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-[4px] bg-[#2eb875]/10 flex items-center justify-center text-[#2eb875]">
          <Send className="size-8" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-[#171c26] tracking-tight">Strategy Received</h3>
          <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-[4px] border-[#171c26]/20 text-[#171c26] hover:bg-[#f0f4f8]">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-semibold text-[#171c26] uppercase tracking-wider text-xs font-mono">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="bg-transparent border-0 border-b border-[#171c26]/20 rounded-none focus:ring-0 focus:border-[#2eb875] transition-all duration-300 px-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-semibold text-[#171c26] uppercase tracking-wider text-xs font-mono">Corporate Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@lavers.com.au" {...field} className="bg-transparent border-0 border-b border-[#171c26]/20 rounded-none focus:ring-0 focus:border-[#2eb875] transition-all duration-300 px-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-semibold text-[#171c26] uppercase tracking-wider text-xs font-mono">Annual Revenue</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="e.g. $500k - $2M" {...field} className="bg-transparent border-0 border-b border-[#171c26]/20 rounded-none focus:ring-0 focus:border-[#2eb875] transition-all duration-300 px-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-semibold text-[#171c26] uppercase tracking-wider text-xs font-mono">Company Name</FormLabel>
              <FormControl>
                <Input placeholder="Your Business Ltd" {...field} className="bg-transparent border-0 border-b border-[#171c26]/20 rounded-none focus:ring-0 focus:border-[#2eb875] transition-all duration-300 px-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Please describe your project"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-semibold text-[#171c26] uppercase tracking-wider text-xs font-mono">Business Pain Points</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your current efficiency challenges and growth goals..." className="min-h-[150px] bg-transparent border border-[#171c26]/20 rounded-[4px] focus:ring-0 focus:border-[#2eb875] transition-all duration-300 resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-[4px] border border-destructive/20 font-mono">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-base font-bold uppercase tracking-widest bg-[#2eb875] hover:bg-[#25915d] text-white transition-all duration-300 rounded-[4px] shadow-[0px_2px_0px_0px_rgba(0,0,0,0.1)] active:shadow-none active:translate-y-[2px]">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Analyzing...
          </> : <>
            Optimize Now <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
};
export default function LetsBuildTheFuture() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="lets-scale-together" className="relative py-24 lg:py-32 bg-[#f0f4f8] text-[#171c26] overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-[#2eb875]/5 blur-[120px]" />
        <div className="absolute top-[40%] -right-[10%] w-[40%] h-[60%] rounded-full bg-[#171c26]/5 blur-[100px]" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div ref={ref} className={`space-y-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="space-y-6">
              <div className="inline-flex items-center px-4 py-1.5 rounded-none border border-[#171c26]/10 bg-white text-[#171c26] text-xs font-mono font-bold uppercase tracking-widest">
                <span className="flex size-2 rounded-full bg-[#2eb875] mr-2 animate-pulse" />
                Strategic Partnership
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold tracking-tighter text-[#171c26]">
                Let's Scale <span className="text-[#2eb875]">Together</span>
              </h1>
              <p className="text-lg text-[#171c26]/70 leading-relaxed max-w-xl font-sans">
                Maximize efficiency and drive sustainable growth with Lavers Business Consulting. 
                With over a decade of experience, we help you cut unnecessary costs and optimize revenue streams.
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex items-start gap-4 group">
                <div className="flex-shrink-0 size-12 rounded-[4px] bg-white border border-[#171c26]/10 shadow-sm flex items-center justify-center text-[#2eb875] group-hover:bg-[#2eb875] group-hover:text-white transition-all duration-300">
                  <MapPin className="size-6" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-bold text-[#171c26] text-lg tracking-tight">Consultancy HQ</h3>
                  <p className="text-[#171c26]/60 mt-1">20 Spring GR, Latrobe, 7308, Australia</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="flex-shrink-0 size-12 rounded-[4px] bg-white border border-[#171c26]/10 shadow-sm flex items-center justify-center text-[#2eb875] group-hover:bg-[#2eb875] group-hover:text-white transition-all duration-300">
                  <Mail className="size-6" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-bold text-[#171c26] text-lg tracking-tight">Direct Inquiry</h3>
                  <Link to="mailto:info@leversbusinessconsulting.com" className="text-[#171c26]/60 mt-1 hover:text-[#2eb875] transition-colors block">
                    info@leversbusinessconsulting.com
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="flex-shrink-0 size-12 rounded-[4px] bg-white border border-[#171c26]/10 shadow-sm flex items-center justify-center text-[#2eb875] group-hover:bg-[#2eb875] group-hover:text-white transition-all duration-300">
                  <Phone className="size-6" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-bold text-[#171c26] text-lg tracking-tight">Expert Advice</h3>
                  <Link to="tel:0468075702" className="text-[#171c26]/60 mt-1 hover:text-[#2eb875] transition-colors block">
                    0468075702
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="flex-shrink-0 size-12 rounded-[4px] bg-white border border-[#171c26]/10 shadow-sm flex items-center justify-center text-[#2eb875] group-hover:bg-[#2eb875] group-hover:text-white transition-all duration-300">
                  <Clock className="size-6" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-bold text-[#171c26] text-lg tracking-tight">Operational Hours</h3>
                  <p className="text-[#171c26]/60 mt-1 font-mono text-sm uppercase">Mon-Fri: 9am - 5pm AEST</p>
                </div>
              </div>
            </div>
          </div>

          <div className={`relative transition-all duration-1000 delay-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="relative rounded-none border border-[#171c26]/10 bg-white shadow-[0px_24px_48px_-12px_rgba(23,28,38,0.08)] p-6 sm:p-8 lg:p-10 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-[#2eb875]/5 to-transparent opacity-50 pointer-events-none" />
              
              <div className="relative z-10">
                <div className="mb-8 border-l-4 border-[#2eb875] pl-6">
                  <h2 className="text-2xl font-bold text-[#171c26] tracking-tight uppercase">Request Performance Audit</h2>
                  <p className="text-muted-foreground mt-2 font-sans">
                    Submit your details for a preliminary efficiency assessment within 24 hours.
                  </p>
                </div>

                <FormProvider formKey="scale_form_consultation" formId="scale_form_consultation" sectionName="lets-scale-together" defaultValues={{
                name: "",
                email: "",
                phone: "",
                company: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                message: {
                  required: true
                }
              }} submitText="Optimize Now" successMessage="Audit request received. A consultant will contact you shortly.">
                  <ContactForm />
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}